import {
  addExternalAccountService,
  approveExternalAccountService,
  declineExternalAccountService,
  deleteExternalAccountService,
  getExternalAccountDetailsService,
  getExternalAccountListService,
} from '@/services/externalAccounts';
import {
  DeclineExternalAccountForm,
  ExternalAccountFormValues,
  ExternalAccountsListQueryParams,
  queryKeys,
} from '@/types/externalAccounts';
import { TransactionRequestSelectionItem } from '@/types/transactionRequests';
import { convertObjectValuesToString } from '@/utils/responseConvertors';
import { AccountBalance } from '@mui/icons-material';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import useIdempotencyKeyGenerator from '../useIdempotencyKeyGenerator';

function clearQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [queryKeys.list],
  });
  queryClient.invalidateQueries({
    queryKey: [queryKeys.details],
  });
}

export const useGetExternalAccountsList = (
  queryParams?: ExternalAccountsListQueryParams,
  enabled = true,
) => {
  const [searchParams] = useSearchParams();
  const params = queryParams
    ? new URLSearchParams(convertObjectValuesToString(queryParams)).toString()
    : searchParams.toString();

  const { data, isPending, isSuccess, refetch } = useQuery({
    queryKey: [queryKeys.list, params],
    queryFn: () => getExternalAccountListService(params),
    enabled,
  });

  const externalAccountsForSelection: TransactionRequestSelectionItem[] = data
    ? data.externalAccounts.records.map(account => {
        const { uuid, accountName, bankCountryCode, bankLogoUrl } = account;

        return {
          value: uuid,
          label: accountName,
          isInternalAccount: false,
          bankAddressCountryCode: bankCountryCode,
          iconUrl: bankLogoUrl,
          icon: bankLogoUrl ? null : AccountBalance,
        };
      })
    : [];

  return {
    isSuccess,
    refetchExternalAccounts: refetch,
    externalAccounts: data?.externalAccounts.records || [],
    externalAccountsForSelection,
    aggregateInfo: data?.aggregateInfo,
    totalRecordCount: data?.externalAccounts.totalRecordCount || 0,
    externalAccountsIsPending: isPending,
  };
};

export const useGetExternalAccountDetails = (id: string) => {
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.details, id],
    queryFn: () => getExternalAccountDetailsService(id),
    enabled: !!id,
  });

  return {
    externalAccountDetails: data,
    isExternalAccountDetailsPending: isPending,
  };
};

export function useAddExternalAccount() {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: ExternalAccountFormValues) =>
      addExternalAccountService({ ...data, idempotencyKey }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    addExternalAccount: mutateAsync,
    isAddExternalAccountPending: isPending,
  };
}

export function useDeleteExternalAccount() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: string) => deleteExternalAccountService(id),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    deleteExternalAccount: mutateAsync,
    isDeleteExternalAccountPending: isPending,
  };
}

export function useDeclineExternalAccount() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: DeclineExternalAccountForm) =>
      declineExternalAccountService(data),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    declineExternalAccount: mutateAsync,
    isDeclineExternalAccountPending: isPending,
  };
}

export function useApproveExternalAccount() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: string) => approveExternalAccountService(id),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    approveExternalAccount: mutateAsync,
    isApproveExternalAccountPending: isPending,
  };
}
